import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "motion/react";
import {
  ArrowLeft, Clock, Eye, Calendar, Hash, Star, Loader2, FileText, CalendarCheck
} from "lucide-react";
import Layout from "../components/layout/Layout";
import { useArticle } from "../hooks/useArticles";

function renderMarkdown(text) {
  if (!text) return "";
  let html = text
    .replace(/^### (.+)$/gm, "<h3 class=\"text-xl font-medium text-white mt-8 mb-3\">$1</h3>")
    .replace(/^## (.+)$/gm, "<h2 class=\"text-2xl font-medium text-white mt-10 mb-4\">$1</h2>")
    .replace(/^# (.+)$/gm, "<h1 class=\"text-3xl font-medium text-white mt-10 mb-4\">$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong class=\"font-medium text-neutral-100\">$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/^\d+\.\s(.+)$/gm, "<li class=\"text-lg font-extralight text-neutral-300 ml-6 list-decimal mb-2\">$1</li>")
    .replace(/^[-*]\s(.+)$/gm, "<li class=\"text-lg font-extralight text-neutral-300 ml-6 list-disc mb-2\">$1</li>")
    .replace(/\n\n/g, "</p><p class=\"text-lg font-extralight text-neutral-300 leading-relaxed mb-4\">")
    .replace(/\n/g, "<br/>");
  html = "<p class=\"text-lg font-extralight text-neutral-300 leading-relaxed mb-4\">" + html + "</p>";
  html = html.replace(/<p[^>]*><h/g, "<h").replace(/<\/h([1-3])><\/p>/g, "</h$1>");
  html = html.replace(/<p[^>]*><li/g, "<li").replace(/<\/li><\/p>/g, "</li>");
  html = html.replace(/<p[^>]*>\s*<\/p>/g, "");
  return html;
}

export default function ArticlePage() {
  const { slug } = useParams();
  const { article, loading } = useArticle(slug);
  const navigate = useNavigate();

  useEffect(() => {
    if (article) {
      document.title = article.meta_title || article.title;
      const metaDesc = document.querySelector("meta[name=\"description\"]");
      if (metaDesc) {
        metaDesc.setAttribute("content", article.meta_description || article.excerpt);
      } else {
        const meta = document.createElement("meta");
        meta.name = "description";
        meta.content = article.meta_description || article.excerpt;
        document.head.appendChild(meta);
      }
      const metaKw = document.querySelector("meta[name=\"keywords\"]");
      if (metaKw) {
        metaKw.setAttribute("content", article.meta_keywords || "");
      } else if (article.meta_keywords) {
        const meta = document.createElement("meta");
        meta.name = "keywords";
        meta.content = article.meta_keywords;
        document.head.appendChild(meta);
      }
    }
    return () => { document.title = "Обстучим"; };
  }, [article]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-10 h-10 text-primary-500 animate-spin" />
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <FileText className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
            <h1 className="text-2xl font-medium text-white mb-4">Статья не найдена</h1>
            <Link
              to="/articles"
              className="text-primary-400 hover:text-primary-300 text-lg font-extralight"
            >
              Вернуться к статьям
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const publishedDate = article.published_at
    ? new Date(article.published_at).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric"
      })
    : "";

  const authorName = article.author?.name || "Эксперт";
  const authorAvatar = article.author?.avatar_url || "";
  const authorTitle = article.author?.title || "";

  const authorSpecializations = article.author?.specializations || [];

  return (
    <Layout>
      <article id="article_page" className="min-h-screen">
        <header className="py-8 lg:py-12 border-b border-neutral-800/30">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link
              to="/articles"
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-extralight">Все статьи</span>
            </Link>

            <div className="flex flex-wrap gap-2 mb-4">
              {(article.tags || []).map((tag) => (
                <Link
                  key={tag}
                  to={`/articles?tag=${tag}`}
                  className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight hover:bg-primary-500/20 transition-colors"
                >
                  <Hash className="w-3.5 h-3.5" />
                  {tag}
                </Link>
              ))}
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-3xl lg:text-4xl font-medium text-white mb-6 leading-tight"
            >
              {article.title}
            </motion.h1>

            {article.excerpt && (
              <p className="text-xl font-extralight text-neutral-400 mb-6 leading-relaxed">
                {article.excerpt}
              </p>
            )}

            <div className="flex items-center justify-between flex-wrap gap-4">
              <Link
                to={`/experts/${article.author_id}`}
                className="flex items-center gap-3 group"
              >
                {authorAvatar ? (
                  <img
                    src={authorAvatar}
                    alt={authorName}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                    <span className="text-white font-medium">{authorName[0]}</span>
                  </div>
                )}
                <div>
                  <p className="text-lg font-medium text-neutral-200 group-hover:text-primary-400 transition-colors">
                    {authorName}
                  </p>
                  {authorTitle && (
                    <p className="text-lg font-extralight text-neutral-500">{authorTitle}</p>
                  )}
                </div>
              </Link>

              <div className="flex items-center gap-4 text-lg font-extralight text-neutral-500">
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>{publishedDate}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  <span>{article.reading_time} мин</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  <span>{article.views_count || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section className="py-8 lg:py-12">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(article.content) }}
            />

            <div className="mt-12 pt-8 border-t border-neutral-800/30">
              <div className="p-6 rounded-md bg-neutral-900/50 border border-neutral-800/40">
                <div className="flex items-start gap-4 mb-5">
                  <Link to={`/experts/${article.author_id}`} className="flex-shrink-0">
                    {authorAvatar ? (
                      <img
                        src={authorAvatar}
                        alt={authorName}
                        className="w-16 h-16 rounded-full object-cover hover:ring-2 hover:ring-primary-500/50 transition-all"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
                        <span className="text-white text-xl font-medium">{authorName[0]}</span>
                      </div>
                    )}
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/experts/${article.author_id}`}
                      className="text-xl font-medium text-white hover:text-primary-400 transition-colors"
                    >
                      {authorName}
                    </Link>
                    {authorTitle && (
                      <p className="text-lg font-extralight text-neutral-400 mt-0.5">{authorTitle}</p>
                    )}
                    {authorSpecializations.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {authorSpecializations.slice(0, 4).map((spec) => (
                          <span
                            key={spec}
                            className="px-2 py-0.5 rounded-md bg-primary-500/10 text-primary-400 text-lg font-extralight"
                          >
                            {spec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <p className="text-lg font-extralight text-neutral-400 mb-5">
                  Понравилась статья? Запишитесь на консультацию к автору и обсудите вашу ситуацию лично
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={() => navigate(`/booking/${article.author_id}`)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-white text-lg font-medium transition-colors"
                  >
                    <CalendarCheck className="w-5 h-5" />
                    Записаться на консультацию
                  </button>
                  <Link
                    to={`/experts/${article.author_id}`}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-md border border-neutral-700 hover:border-neutral-500 text-neutral-300 hover:text-white text-lg font-extralight transition-colors"
                  >
                    Профиль эксперта
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </article>
    </Layout>
  );
}
